/**
 * Creates a view (for modal dialog) to display information to the user
 * when an action for a domain object cannot be performed.
 *
 * Use cases:
 * - An object cannot be deleted because things depend on it (e.g. scorecard
 * items) (#12182)
 * - Preventing save for groups having datasets but without org permissions
 * (#36186)
 *
 * Usage:
 *   new DependenciesPreventActionView({model, response:
 * response.responseJSON})
 */

import Backbone from 'backbone'

import template from './dependencies-prevent-action-view.hbs'
import _ from 'lodash'
import i18n from 'scoreboard/components/i18n'
import Dataset from 'scoreboard/datasets/model/dataset'
import metadata from 'scoreboard/components/metadata'
import sections from 'scoreboard/components/sections'
import appDisplay from 'scoreboard/components/app-display'
import errors from 'scoreboard/components/setup-errors'
import ErrorView from 'scoreboard/components/error-view'
import {isOffline} from 'scoreboard/components/xhr'
import highlight from 'scoreboard/components/highlight'
import DatasetsFilterBadges from 'scoreboard/datasets/filters/datasets-filter-badges'
import datasetPaths from 'scoreboard/datasets/edit/dataset-paths'
import guard from 'scoreboard/components/unsaved/guard'
import unwrapModel from 'scoreboard/components/unwrap-model'
import constants from 'scoreboard/components/constants'
const { DATABASE, FTP, GOOGLE_SHEETS, ZAPIER, WEB_SERVICE } = constants
import DatasetDependencySubType
	from 'scoreboard/components/dataset-dependency-sub-type'
import DomainType from 'scoreboard/components/domain-type'
import MenuManager from 'scoreboard/menu-manager'

var DependenciesPreventActionView = Backbone.View.extend({

	className: 'dependencies-prevent-action-view',

	events: {
		'click .sp-navigate': 'onClickNavigate',
		'click .cancel': 'onClickCancel',
		'click .save': 'onClickSave',
		'click .dont-save': 'onClickDontSave'
	},

	/**
	 * @typedef PreventedActionOptions
	 *
	 * @property {Backbone.Model|Object} options.model the object being acted
	 *     upon
	 *
	 * @property {String} [options.title] a title for the dialog (already
	 *     translated)
	 *
	 * @property {String} [options.header='Items Preventing Deletion'] i18n
	 *     code or text If the value is null (not undefined, null) then the
	 *     header will be omitted
	 *
	 * @property {Object} [options.response] The XHR response json object
	 *
	 * @property {Object}
	 *     [options.response.actionFailedReason='common.error.cannotDeleteDueToLinkedItems']
	 *     The banner message
	 *
	 * @property {Boolean} [options.useIcon]
	 *
	 * @property {FormView} [options.formView] If a FormView is provided, then
	 *     cancel and save buttons will be displayed and associated to the form
	 *     submit/cancel actions.  If no form is provided a single cancel
	 *     button will close the dialog
	 *
	 * @property {Boolean} [options.showDontSave=false] If there is a formView
	 * 	this can optionally be set to true to show a `Don't Save` button
	 */

	/**
	 * @constructs
	 * @param {PreventedActionOptions} options
	 */
	initialize: function(options = {}) {
		this.options = options || {}
		this.formView = this.options.formView
		this.model = this.options.model
		this.response = this.options.response || {}
		this.useIcon = this.options.useIcon
		this.treeItemsById = _.keyBy(this.options.response.treeItems, 'id')
	},

	render: function() {
		this.$el.empty().append(template(this.context()))
		// prevent nested .sp-navigate classes from causing 2 navigation events to be fired
		// when a link within a dependency item is clicked.
		this.$('.dependency-item').each((i, item) => {
			item = this.$(item)
			if (!item.attr('href')) {
				item.removeClass('sp-navigate')
				item.removeAttr('href')
			}
		})

		if (this.options.showDontSave !== true) {
			this.$('.dont-save').remove()
		}

		return this
	},

	context: function () {
		// #54002 if 'treeLabel' is defined (ie for a rollup tree), use that for the label in the title
		const {label, treeLabel} = unwrapModel(this.model)
		const labelToUse = !_.isUndefined(treeLabel) ? treeLabel : label
		const title = this.options.title || i18n.get('common.error.cannotDelete', labelToUse)
		const reason = this.response.actionFailedReason || i18n.get('common.error.cannotDeleteDueToLinkedItems')

		let {header} = this.options
		if (!_.isNull(header)) {
			header = i18n.get(header || 'common.error.cannotDelete.linkedFields')
		}

		const dependencies = _.cloneDeep(this.response.dependencies)
		this._decorateDependencies(dependencies)

		return _.extend({}, this.options, {
			title, header, dependencies, reason
		})
	},

	_decorateDependencies: function (dependencies) {
		if (!dependencies || dependencies.length === 0) {
			// #30508 No dependencies to show; simplify the dialog
			this.$el.addClass('dialog-message')
		} else {
			// set the icon and the link for each item
			_.each(dependencies, (item) => {
				item.icon = item.type
				item.extraClass = ''
				item.highlightedLabel = highlight(item.label)
				const urlPrefix = this.getUrlPrefix(item)
				item.href = urlPrefix + '/' + item.id
				item.highlightedLabel = highlight(item.label)
				item.highlightedColumnLabel = highlight(item.columnLabel)
				_.forEach(_.values(this.model.datasetsById), dataset => {
					const vlf = dataset.visibleLinkedFieldsById[item.visibleLinkedFieldId]
					if (vlf) {
						item.highlightedVlfLabel = highlight(vlf.label)
					}
					const field = dataset.combinedFieldsById[item.fieldId]
					if (field) {
						item.highlightedFieldLabel = highlight(field.label)
					}
				})
				if (this.model instanceof Dataset) {
					this.decorateDependencyForDatasetDeletion(item)
				}
				if (item.domainType === DomainType.EMBEDDABLE_RESOURCE) {
					item.icon = 'dashboard'
					item.href = 'admin/shared-dashboards'
				}
				// #18505 include any datasets that link to the dataset in list
				// of dependencies
				if (item.domainType === DomainType.DATASET) {
					this.buildDatasetDependency(item)
				}
				if (item.domainType === DomainType.DATASET_FIELD) {
					this.buildDatasetFieldDependency(item)
				}
				if (item.domainType === DomainType.SCORECARD_ITEM) {
					// #50666 only show datasets detail message if this is actually
					// for a kpi from a dataset, so we don't get the incorrect
					// message when deleting other domain objects
					if (item.type === 'metric' && !_.isNil(item.linkedDataset)) {
						item.detailMessage = i18n.get(
							'datasets.delete.dependencies.metric',
							item.highlightedLabel)
					}

					if (!_.isNil(item.subType)) {
						item.icon = 'scorecard'
					}
					if (item.subType === DatasetDependencySubType.SERIES_VLF) {
						item.detailMessage = i18n.get(
							'datasets.delete.dependencies.visibleLinkedFields.scorecard',
							highlight(i18n.get(item.seriesName)),
							item.highlightedVlfLabel,
							item.highlightedLinkedDatasetLabel)
					}
					if (item.subType === DatasetDependencySubType.REPORT_CHART) {
						item.detailMessage = i18n.get(
							'datasets.delete.dependencies.visibleLinkedFields.chart',
							item.highlightedVlfLabel,
							item.highlightedLinkedDatasetLabel)
					}
				}
				if (item.domainType === DomainType.DASHBOARD) {
					this.buildDashboardDependency(item)
				}
				if (item.domainType === DomainType.REPORT) {
					item.icon = 'report'
					item.href = `/reports/${item.id}`
					if (item.type === 'dataset-report-designer') {
						if (item.subType === DatasetDependencySubType.REPORT_COLUMN_FIELD ||
							item.subType === DatasetDependencySubType.REPORT_COLUMN_EQUATION_FIELD) {
							let msgKey = 'datasets.dependencies.report.field'
							if (item.subType === DatasetDependencySubType.REPORT_COLUMN_EQUATION_FIELD) {
								msgKey += '.equation'
							}
							item.detailMessage = i18n.get(msgKey, item.highlightedLabel,
								item.highlightedColumnLabel, item.highlightedFieldLabel,
								item.highlightedDatasetLabel)
							return
						}
						if (item.subType === DatasetDependencySubType.REPORT_VISIBLE_LINKED_FIELD) {
							item.detailMessage = i18n.get('datasets.dependencies.report.visibleLinkedField',
								item.highlightedLabel, item.highlightedLabel, item.highlightedDatasetLabel)
							return
						}
						item.detailMessage = i18n.get(
							'datasets.delete.dependencies.reportDesignerColumns',
							item.highlightedLabel, item.highlightedColumnLabel,
							item.highlightedDatasetLabel
						)
						return
					}

					if (item.subType === DatasetDependencySubType.DASHBOARD_CHART ||
						item.subType === DatasetDependencySubType.REPORT_CHART ||
						item.subType === DatasetDependencySubType.SCORECARD_CHART) {
						item.detailMessage = i18n.get(
							'datasets.delete.dependencies.visibleLinkedFields.chart',
							item.highlightedFieldLabel,
							item.highlightedLinkedDatasetLabel)
					}
				}
				// Organization.java does not have a type property, so neither
				// do it's dependencies
				if (item.domainType === DomainType.ORGANIZATION) {
					item.icon = 'circle-fill'
					item.href = `organizations/${item.id}/scorecards`

					if (item.subType === 'dataset-field') {
						let filters = [item.filter]
						const filterBadge = new DatasetsFilterBadges({
							model: item.dataset,
							filters,
							isEditable: false,
							section: 'datasets',
							treeItemsById: this.treeItemsById
						}).render()

						if (filters.length > 1) {
							item.detailMessage = i18n.get(
								'datasets.delete.dependencies.orgTemplate.complexFilter1',
								item.highlightedLabel,
								item.highlightedFieldLabel,
								item.highlightedDatasetLabel,
								filterBadge.el.innerHTML)
							item.detailMessage += '</br>' + i18n.get(
								'datasets.delete.dependencies.orgTemplate.complexFilter2',
								filterBadge.el.innerHTML)
							item.extraClass += ' has-multiple-filters'
						} else {
							item.detailMessage = i18n.get(
								'datasets.delete.dependencies.orgTemplate',
								filterBadge.el.innerHTML,
								item.highlightedLabel,
								item.highlightedFieldLabel,
								item.highlightedDatasetLabel)
						}
					}
				}
				if (item.domainType === DomainType.SCHEDULED_IMPORT ||
					item.domainType === DomainType.VALUE_IMPORT) {
					item.href = 'admin/imports/' + item.id
					item.icon = 'docs-spreadsheet-mono'
				}
				if (item.domainType === DomainType.FORM) {
					item.href = 'forms/' + item.id
					item.icon = 'form'
					this.buildFormWidgetDependencyDetailMessage(item)
				}
				if (item.domainType === DomainType.CHART && item.subType) {
					// these dependencies are always the current dataset
					const highlightedDatasetLabel = highlight(this.model.get('label'))
					if (item.subType === 'field') {
						item.detailMessage = i18n.get(
							'datasets.dependencies.chart.axisFieldId',
							item.highlightedLabel, highlight(item.axis),
							item.highlightedFieldLabel, highlightedDatasetLabel
						)
					}
					if (item.subType === DatasetDependencySubType.VISIBLE_LINKED_FIELD) {
						item.detailMessage = i18n.get(
							'datasets.dependencies.chart.axisVlfId',
							item.highlightedLabel, highlight(item.axis),
							item.highlightedVlfLabel, highlightedDatasetLabel
						)
					}
				}
				if (item.domainType === DomainType.SCORECARD_ITEM && item.subType) {
					const highlightedDatasetLabel = highlight(this.model.get('label'))
					if (item.subType === 'field') {
						item.detailMessage = i18n.get(
							'datasets.dependencies.scorecard.chartdesigner.axisFieldId',
							item.highlightedLabel, highlight(item.axis),
							item.highlightedFieldLabel, highlightedDatasetLabel
						)
					}
				}

				if (item.type === 'spreadsheet') {
					item.icon = 'docs-spreadsheet-mono'
				}
				if (item.icon === 'document') {
					item.icon = 'document-blank'
				}
				if (_.isNil(item.detailMessage)) {
					item.extraClass += ' title-only'
				}
			})
		}
	},

	getUrlPrefix: function(item) {
		let section = _.get(metadata[item.domainType], 'section')
		return sections.toUrl(section)
	},

	/**
	 * Decorates a dependency item with additional information when a dataset
	 * is being deleted.  Extracted from _decorateDependencies since
	 * that method was getting a little long.
	 */
	decorateDependencyForDatasetDeletion: function(item) {
		// for LinkedDatasetDependency that has a linkedDatasetId
		if (item.linkedDatasetId) {
			item.linkedDataset = this.model.datasetsById[item.linkedDatasetId]
			item.highlightedLinkedDatasetLabel =
				item.linkedDataset && highlight(item.linkedDataset.get(
					'label'))
		}
		// for LinkedDatasetDependency with no linkedDatasetId
		if (item.visibleLinkedFieldId || item.fieldId) {
			let datasetId
			const field = this.model.combinedFieldsById[item.fieldId]
			if (field) {
				datasetId = field.datasetId
			}
			const vlf = this.model.visibleLinkedFieldsById[item.visibleLinkedFieldId]
			// if we don't have a datasetId from the item's fieldId, then check
			// for a vlf
			if (!datasetId && vlf) {
				datasetId = this.model.id
			}

			const dataset = this.model.datasetsById[datasetId]
			if (dataset) {
				item.highlightedDatasetLabel = highlight(dataset.get('label'))
			}
		}
		// for OrgTemplateDatasetDependency
		if (item.datasetId) {
			item.dataset = this.model.datasetsById[item.datasetId]
			item.highlightedDatasetLabel =
				item.dataset && highlight(item.dataset.get('label'))
		}
		// for a scorecard item historical chart referencing a dataset field
		// #53374 #53373
		if (item.domainType === DomainType.CHART && item.type === 'scorecard-item') {
			item.icon = 'scorecard'
			item.href = _.get(metadata[item.type], 'section') + '/' + item.id
		}
		// #50990 for a chart dependency when deleting a dataset
		if (item.domainType == 'chart' && item.type == 'chart' && !item.subType) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.chart',
				item.highlightedLabel)
		}
		// #50990 for a report dependency when deleting a using a dataset
		if (item.domainType =='report' && item.type=='report' && !item.subType) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.report',
				item.highlightedLabel)
		}
	},

	buildDatasetDependency: function(item) {
		// #52130 when deleting a file, no additional formatting is required
		// for a dataset dependency
		// #47529 prevent js error from admin/dataset rollup trees
		// #54002 no additional message or formatting is needed when
		// attempting to delete a dataset rollup tree
		// since the rollup tree model does not have a 'type' populated,
		// we'll check to see if `treeLabel` is populated

		const {type, treeLabel} = unwrapModel(this.model)
		if (type === 'file' || !_.isUndefined(treeLabel)
			// #55109 no additional formatting is needed for a dataset dependency
			// when deleting an import connection
			|| this.isImportConnection(type)) {
			return
		}
		const urlPrefix = this.getUrlPrefix(item)
		item.icon = Dataset.icon(item.type)
		if (item.type === 'dataset' && !item.subType) {
			item.detailMessage = i18n.get('datasets.unlink.dependencies.dataset',
				item.highlightedLabel)
		}

		let msgKey, label, dependencyItemLink
		if (item.subType === DatasetDependencySubType.VISIBLE_LINKED_FIELD) {
			msgKey = 'datasets.delete.dependencies.visibleLinkedField'
			label = item.highlightedVlfLabel

			// #36056 if we're unlinking the current dataset from another dataset, but there's
			// still a VLF that uses a field from the other dataset, then show that dataset in the link.
			if (item.id === item.linkedDatasetId && item.fieldId) {
				const vlf = this.model.visibleLinkedFieldsById[item.visibleLinkedFieldId]
				// #36549 if the VLF is the native total, then it won't have a fieldId, so
				// we need to look it up by its path instead, and we can just do this for all vlfs
				const unlinkedDatasetId = datasetPaths.lastDatasetId(vlf.path)
				const unlinkedDataset = this.model.datasetsById[unlinkedDatasetId]

				dependencyItemLink = this._dependencyItemLink({
					id: unlinkedDatasetId,
					href: `${urlPrefix}/${unlinkedDatasetId}`,
					label: unlinkedDataset.get('label')
				})
			}

			// #36056 if there's a vlf whose linked dataset refers to a field in the current
			// dataset, and it isn't a VLF in the current dataset itself, we show a different
			// message and no link to the dataset we're already on.
			if (item.linkedDatasetId === this.model.id && item.id !== item.linkedDatasetId) {
				msgKey += '.own'
				dependencyItemLink = this._dependencyItemLink({
					id: item.id,
					href: `${urlPrefix}/${item.id}/edit`,
					label: item.label
				})
			}
		}
		if (item.subType === DatasetDependencySubType.FIELD_EQUATION) {
			msgKey = 'datasets.delete.dependencies.fieldEquation'
			label = item.highlightedFieldLabel
		}
		if (msgKey) {
			item.href = null
			if (!dependencyItemLink) {
				dependencyItemLink = this._dependencyItemLink({
					id: item.linkedDatasetId,
					href: `${urlPrefix}/${item.linkedDatasetId}/edit`,
					// pass in the un-highlighted version since _dependencyItemLink handles highlighting/escaping
					label: item.linkedDataset.get('label')
				})
			}
			item.detailMessage = i18n.get(msgKey, label, dependencyItemLink)
		}

		// calendars
		if (item.subType === DatasetDependencySubType.DEFAULT_CALENDAR_VLF_ID ||
			item.subType === DatasetDependencySubType.SELECTED_CALENDAR_VLF_ID) {
			item.icon = 'calendar'
			const calendarType =
				item.subType === DatasetDependencySubType.DEFAULT_CALENDAR_VLF_ID  ?
					'default' : ''
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.calendar',
				highlight(calendarType), item.highlightedVlfLabel, item.highlightedLinkedDatasetLabel)
		}
		// total display
		if (item.subType === DatasetDependencySubType.AGGREGATION_VLF_ID ) {
			const aggregationFieldType = this.model.get('rootDatasetId') === item.id ? 'default ' : ''
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.totalDisplay',
				highlight(aggregationFieldType), item.highlightedVlfLabel, item.highlightedLinkedDatasetLabel)
		}

		// dataset tables
		if (item.type === 'view') {
			this.buildTableDependency(item)
			if (item.subType === DatasetDependencySubType.DATASET_AGGREGATION) {
				this.buildTableColumnDependency(item)
			}
			if (item.subType === DatasetDependencySubType.FILTER_VLF_ID) {
				const filterBadge = new DatasetsFilterBadges({
					model: item.linkedDataset,
					filters: [item.filter],
					isEditable: false,
					section: 'datasets'
				}).render()
				item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.view.filter',
					filterBadge.el.innerHTML, item.highlightedVlfLabel, item.highlightedLinkedDatasetLabel)
			}
			if (item.subType === DatasetDependencySubType.DATASET_AGGREGATIONS_FIELD_ID) {
				item.detailMessage = i18n.get('datasets.unlink.dependencies.datasetTables.tableFieldId',
					item.highlightedFieldLabel, item.highlightedLinkedDatasetLabel, highlight(item.equation))
			}
		}

		// permissions filters
		if (item.subType === DatasetDependencySubType.PERMISSIONS_FILTER) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.permissions.filter',
				highlight(item.principal.label), item.highlightedVlfLabel, item.highlightedLinkedDatasetLabel)
			item.href = urlPrefix + '/' + item.linkedDatasetId
		}
	},

	buildDatasetFieldDependency: function(item) {
		// calculated fields that reference other calculated fields
		if (item.subType === DatasetDependencySubType.CALCULATED_FIELD) {
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.calculatedField',
				item.highlightedFieldLabel, item.highlightedLabel)
		}
		// #58261 - the icon should be 'dataset'
		item.icon = 'dataset'
		// #58262 the href should go to the dataset the field belongs to
		let datasetId = this.model.id
		if (!_.isNil(item.fieldId)) {
			const field = this.model.combinedFieldsById[item.fieldId]
			if (!_.isNil(field) && !_.isNil(field.datasetId)) {
				datasetId = field.datasetId
			}
		}
		item.href = `datasets/${datasetId}`
	},

	buildDashboardDependency: function(item) {
		item.icon = 'dashboard'

		if (!_.isNil(item.chartDesignerId)) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.dashboard.chart',
				item.highlightedLabel)
			return
		}

		if (item.type === 'dashboard') {
			item.detailMessage = i18n.get('datasets.delete.dependencies.dashboard',
				item.highlightedLabel)
			return
		}
		// #50690 provide better detail message for a shape widget based on a
		// dataset field
		if (item.subType === DatasetDependencySubType.SHAPE_WIDGET) {
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.valueWidget',
				item.highlightedLabel, item.highlightedFieldLabel)
			return
		}
		if (item.type === DatasetDependencySubType.TABLE_WIDGET) {
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.datasetTable',
				item.highlightedLabel, item.highlightedFieldLabel)
			return
		}
		if (item.type === DatasetDependencySubType.TABLE_WIDGET_COLUMN) {
			if (item.subType === DatasetDependencySubType.TABLE_WIDGET_COLUMN_EQUATION) {
				item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.datasetTableColumn.equation',
					item.highlightedLabel, item.highlightedFieldLabel)
				return
			}
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.datasetTableColumn',
				item.highlightedLabel, item.highlightedFieldLabel)
			return
		}
		// #53941 provide detail message when item.type = DATASET_DEPENDENCY_SUB_TYPE.TABLE_WIDGET_COLUMN_EQUATION
		if (item.type === DatasetDependencySubType.TABLE_WIDGET_COLUMN_EQUATION) {
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.datasetTableColumn.equation',
				item.highlightedLabel, item.highlightedFieldLabel)
			return
		}
		if (item.type === DatasetDependencySubType.FILTER_WIDGET) {
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.filterWidget',
				item.highlightedLabel, item.highlightedFieldLabel)
			return
		}
		if (item.subType === DatasetDependencySubType.DASHBOARD_ONLY_DATASET_REPORT_DESIGNER) {
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.dashboard.reportDesignerColumns',
				item.highlightedLabel, item.highlightedColumnLabel)
			return
		}
		if (item.type === 'chart') {
			item.detailMessage = i18n.get('datasets.delete.dependencies.chartDesignerBreakdown.dashboard',
				item.highlightedLabel)
			return
		}

		this.buildTableDependency(item)
		if (item.subType === DatasetDependencySubType.FILTER_WIDGET ||
			item.subType === DatasetDependencySubType.TABLE_WIDGET ||
			item.subType === DatasetDependencySubType.VALUE_WIDGET) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.dashboard',
				highlight(this._getSubType(item.subType)), item.highlightedVlfLabel, item.highlightedLinkedDatasetLabel)
		}
		if (item.subType === DatasetDependencySubType.DASHBOARD_CHART ||
			item.subType === DatasetDependencySubType.SCORECARD_CHART ||
			item.subType === DatasetDependencySubType.REPORT_CHART) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.chart',
				item.highlightedVlfLabel, item.highlightedLinkedDatasetLabel)
		}
		if (item.subType === DatasetDependencySubType.TABLE_WIDGET_COLUMN) {
			this.buildTableColumnDependency(item)
		}
	},

	buildTableDependency: function(item) {
		const urlPrefix = this.getUrlPrefix(item)
		if (item.subType === DatasetDependencySubType.TABLE_VLF_ID) {
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.tables.mainField',
				item.highlightedVlfLabel, highlight(this.model.get('label')))
		}
		if (item.subType === DatasetDependencySubType.COLUMN_BREAKDOWN_VLF_ID ||
			item.subType === DatasetDependencySubType.ROW_BREAKDOWN_VLF_ID) {
			const breakdown =
				item.subType === DatasetDependencySubType.COLUMN_BREAKDOWN_VLF_ID
					? 'column' : 'row'
			const label = _.isNil(item.linkedDataset) ? '' : item.linkedDataset.get('label')
			const datasetLink = this._dependencyItemLink({
				id: item.linkedDatasetId,
				href: `${urlPrefix}/${item.linkedDatasetId}`,
				label
			})
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.tables.breakdown',
				item.highlightedFieldLabel, datasetLink, item.highlightedVlfLabel, highlight(breakdown))
		}
	},

	buildTableColumnDependency: function(item) {
		if (item.linkedDatasetId) {
			// message when tables use linked datasets/visible linked fields
			const link = this._dependencyItemLink({
				id: item.linkedDatasetId,
				href: `dataset/${item.linkedDatasetId}`,
				label: item.linkedDataset.get('label')
			})
			item.detailMessage = i18n.get('datasets.delete.dependencies.visibleLinkedFields.tables.column',
				item.highlightedFieldLabel, link, item.highlightedVlfLabel)
		} else {
			// message for a field used in calculated table column
			item.detailMessage = i18n.get('datasets.delete.field.dependencies.view.table.column', item.highlightedLabel,
				item.highlightedFieldLabel, item.highlightedDatasetLabel)
		}

	},

	_getSubType: function(subType) {
		let msgKey
		if (subType === DatasetDependencySubType.VALUE_WIDGET) {
			msgKey = 'datasets.delete.dependencies.widget.value'
		} else if (subType === DatasetDependencySubType.FILTER_WIDGET) {
			msgKey = 'datasets.delete.dependencies.widget.filter'
		} else if (subType === DatasetDependencySubType.TABLE_WIDGET) {
			msgKey = 'datasets.delete.dependencies.widget.table'
		}
		return i18n.get(msgKey)
	},

	/**
	 * Generates a link in the dependency item's detailMessage
	 * @param {Object} options
	 * @param {number} options.id
	 * @param {string} options.href
	 * @param {string} options.label un-highlighted/escaped label
	 * @returns {string}
	 * @private
	 */
	_dependencyItemLink: function(options) {
		return `<a data-id="${options.id}" class="sp-navigate" href="${options.href}">
					${highlight(options.label)}
				</a>`
	},

	/**
	 * Builds the item's detailMessage for form widget dependencies
	 */
	buildFormWidgetDependencyDetailMessage: function(item) {
		if (this.model instanceof Dataset) {
			let msgKey
			if (item.subType === DatasetDependencySubType.RECORD_LIST_WIDGET_COLUMN) {
				// a dataset field cannot be deleted due to usage for a form's
				// record list
				msgKey = 'datasets.delete.field.dependencies.form.recordListWidgetColumn'
				item.detailMessage = i18n.get(msgKey,
					item.highlightedLabel, highlight(item.formPageLabel),
					highlight(item.widgetLabel), item.highlightedFieldLabel)
			} else if (item.subType === DatasetDependencySubType.FORM_WIDGET) {
				// a dataset link cannot be changed due to use of a field from
				// the linked dataset by a form widget
				if (_.isNil(item.widgetLabel) || item.widgetLabel === '') {
					msgKey = 'datasets.delete.field.dependencies.form.formWidget'
					item.detailMessage = i18n.get(msgKey,
						item.highlightedLabel, highlight(item.formPageLabel),
						item.highlightedFieldLabel)
				} else {
					msgKey = 'datasets.delete.field.dependencies.form.formWidget.withLabel'
					item.detailMessage = i18n.get(msgKey,
						item.highlightedLabel, highlight(item.widgetLabel),
						highlight(item.formPageLabel), item.highlightedFieldLabel)
				}
			}
		}
	},

	isImportConnection: function(type) {
		let connectionTypes = [GOOGLE_SHEETS, FTP, DATABASE, WEB_SERVICE, ZAPIER]
		return _.includes(connectionTypes, type)
	},

	onClickNavigate(e) {
		if (this.formView) {
			const detectDirtyFn = () => true
			const continueFn = () => this.trigger('save')
			// formView.js removes the guard so we have to add one here
			guard.guard(detectDirtyFn, continueFn)
		}
	},

	onClickCancel() {
		_.invoke(this.formView, 'resetForm')
		this.close()
	},

	onClickSave() {
		this.trigger('save')
		this.close()
	},

	onClickDontSave() {
		this.trigger('dont-save')
		this.close()
	},

	close() {
		this.trigger('close')
		// Make sure the org pane closes after the item which was navigated to is
		// shown and this dialog is closed. We need to wait for the next tick in order
		// call menu-manager:hideOrganizationPane() because the org pane will not close
		// if there's a dialog visible and this is an outlying case.
		process.nextTick(() => MenuManager.hideOrganizationPane())
	}

}, {

	/**
	 * Creates an error handler for use with jQuery error callbacks
	 * @param {Backbone.Model|Object} model the model to use for the
	 * DependenciesPreventDeletionView
	 * @param {String} [title] optional title to use for the dialog, if the
	 * default title isn't desired
	 * @returns {Function} the error handler
	 */
	createjQueryErrorHandler: function({ model, title }) {
		return xhr => {
			if (isOffline(xhr)) {
				// #31389 if an offline error occurs, a dialog has already been
				// displayed, so don't show a second one
				return
			}
			if (_.has(xhr.responseJSON, 'actionFailedReason')) {
				appDisplay.replaceDialog(new DependenciesPreventActionView({
					title,
					model,
					response: xhr.responseJSON,
					useIcon: true
				}))
			} else {
				appDisplay.replaceDialog(new ErrorView(
					errors.errorViewOptions(xhr.status, xhr.responseJSON)
				))
			}
		}
	},

	/**
	 * Creates an error handler for use with Backbone error callbacks
	 * @param {String} [title] optional title to use for the dialog, if the
	 * default title isn't desired
	 * @returns {Function} the error handler
	 */
	createBackboneErrorHandler: function({ title } = {}) {
		return (model, xhr) => {
			DependenciesPreventActionView.createjQueryErrorHandler({ model, title })(xhr)
		}
	}

})

export default DependenciesPreventActionView

DependenciesPreventActionView.__filename__ = 'components/edit/dependencies-prevent-action-view.js'
